import React from 'react';
import propType from 'prop-types';
import classNames from 'classnames/bind.js';
import {
  useDataModel,
  params,
  string,
  bool,
  arrayOf,
  client,
  shape, number
} from '@thd-nucleus/data-sources';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import Quantity from '../Quantity';
import styles from './ProductQuantity.module.scss';

const MERCHANDISE = 'MERCHANDISE';
const HOME_DEPOT_QUOTE_CENTER = 'HDQC';
const cx = classNames.bind(styles);

export const ProductQuantity = ({
  itemId,
  onQuantityChange,
  quantity,
  showQtyText,
  showQtyValueText,
  showQtyValueTextWide,
  mediumWidth,
  noMargin,
  hideIfATCDisabled,
  hideForGenericProduct,
  hideForNonMerchandise,
  hideForHDQCSku
}) => {
  const { data, loading, error } = useDataModel('product', {
    variables: {
      itemId
    }
  });
  const wrapperClassNames = cx(
    'quantity-box__wrapper',
    {
      'quantity-box__medium-width': mediumWidth,
      'quantity-box__no-margin': noMargin
    }
  );

  const inputWrapperClassNames = cx(
    'quantity-box__input-wrapper',
    {
      'quantity-box__input-medium-width': mediumWidth
    }
  );

  const quantityValueTextClassNames = cx(
    {
      'quantity-box__text-quantity--wide': showQtyValueTextWide
    }
  );

  const quantityTextClassNames = cx(
    'quantity-box-text',
    {
      'quantity-box-text--wide': showQtyValueTextWide
    }
  );

  const { product } = data || {};
  const { identifiers, info } = product || {};
  const { productType } = identifiers || {};
  let showQuantityBox = false;
  const shouldHideIfATCDisabled = hideIfATCDisabled && !podFulFillmentUtils.isATCEnabled(product);
  const shouldHideForGenericProduct = hideForGenericProduct && !!(product?.info?.isGenericProduct);
  const shouldHideForNonMerchandise = hideForNonMerchandise && productType !== MERCHANDISE;
  const shouldHideForHDQCSku = hideForHDQCSku && info?.productSubType?.name === HOME_DEPOT_QUOTE_CENTER;
  if (product) {
    showQuantityBox = !(
      shouldHideIfATCDisabled
      || shouldHideForGenericProduct
      || shouldHideForNonMerchandise
      || shouldHideForHDQCSku
    );
  }
  const quantityBoxTestId = noMargin ? 'quantity-box-wrapper_no-margin' : 'quantity-box-wrapper';
  if (!showQuantityBox) {
    return (
      <div
        data-testid="quantity-box-wrapper-empty"
        data-component="ProductQuantity"
        className="sui-h-13"
      />
    );
  }

  const quantityTextTestId = showQtyValueTextWide ? 'quantity-text-wide' : 'quantity-text';
  const showQuantValue = showQtyValueTextWide ? 'quantity-text-wide-quantity' : 'quantity-text';
  const inputWidthTestId = mediumWidth ? 'input-medium-width' : 'input-regular-width';
  return ((showQtyText ? (
    <div
      data-testid={quantityBoxTestId}
      className={wrapperClassNames}
      data-component="ProductQuantity"
    >
      <div
        data-testid={quantityTextTestId}
        className={quantityTextClassNames}
      >
        <span>Qty: </span>
        {showQtyValueText && (
          <span
            data-testid={showQuantValue}
            className={quantityValueTextClassNames}
          >{quantity}
          </span>
        )}
      </div>
      {
        !showQtyValueText && (
          <div
            data-testid={inputWidthTestId}
            className={inputWrapperClassNames}
          >
            <Quantity
              value={quantity}
              onChange={onQuantityChange}
              fullWidth
            />
          </div>
        )
      }
    </div>
  ) : (
    <div data-testid="quantity-box-wrapper" data-component="ProductQuantity">
      <Quantity
        value={quantity}
        onChange={onQuantityChange}
        fullWidth
      />
    </div>
  ))
  );
};

ProductQuantity.propTypes = {
  itemId: propType.string.isRequired,
  hideIfATCDisabled: propType.bool,
  hideForGenericProduct: propType.bool,
  hideForNonMerchandise: propType.bool,
  hideForHDQCSku: propType.bool,
  mediumWidth: propType.bool,
  noMargin: propType.bool,
  onQuantityChange: propType.func,
  quantity: propType.number,
  showQtyText: propType.bool,
  showQtyValueText: propType.bool,
  showQtyValueTextWide: propType.bool,
};

ProductQuantity.defaultProps = {
  hideIfATCDisabled: true,
  hideForGenericProduct: true,
  hideForNonMerchandise: true,
  hideForHDQCSku: true,
  onQuantityChange: () => { },
  quantity: 1,
  showQtyText: false,
  showQtyValueText: false,
  showQtyValueTextWide: false,
  mediumWidth: false,
  noMargin: false
};

ProductQuantity.displayName = 'ProductQuantity';

ProductQuantity.dataModel = {
  product: params({
    itemId: string().isRequired()
  }).shape({
    itemId: string(),
    dataSources: string(),
    info: shape({
      isGenericProduct: bool(),
      hidePrice: bool(),
      productSubType: shape({
        name: string()
      })
    }),
    identifiers: shape({
      productType: string()
    }),
    fulfillment: client(params({ storeId: string(), zipCode: string() }).shape({
      fulfillmentOptions: arrayOf(shape({
        type: string(),
        fulfillable: bool(),
        services: arrayOf(shape({
          type: string(),
          locations: arrayOf(shape({
            isAnchor: bool(),
            state: string(),
            inventory: shape({
              isInStock: bool(),
              isOutOfStock: bool(),
              isLimitedQuantity: bool(),
              quantity: number()
            })
          }))
        }))
      })),
      bossExcludedShipStates: string(),
      excludedShipStates: string()
    })),
    availabilityType: shape({
      type: string(),
      discontinued: bool()
    })
  })
};
